import React, { useEffect, useMemo, useState } from 'react';
import {
  App,
  Button,
  Card,
  Descriptions,
  Image,
  Input,
  Modal,
  Space,
  Table,
  Tag,
} from 'antd';
import { ReloadOutlined, CheckOutlined, EyeOutlined } from '@ant-design/icons';
import { axios } from 'lib/axios';
import PageShell, { useBrandToken } from 'components/PageShell';

const fileUrl = (f) => {
  if (!f) return null;
  if (typeof f === 'string') return f;
  return f.file_name || f.url || f.path || null;
};

const fileList = (...groups) => {
  const out = [];
  groups.forEach((g) => {
    (Array.isArray(g) ? g : []).forEach((f) => {
      const u = fileUrl(f);
      if (u) out.push(u);
    });
  });
  return out;
};

const MerchantVerification = () => {
  const { message } = App.useApp();
  const colors = useBrandToken();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null);
  const [search, setSearch] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const fetchPending = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/outlet', { params: { limit: 100 } });
      const list = Array.isArray(res?.data) ? res.data : [];
      setRows(list.filter((o) => !o.is_verified));
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPending();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((o) =>
      `${o.name || ''} ${o.user?.full_name || ''} ${o.city || ''}`
        .toLowerCase()
        .includes(q),
    );
  }, [rows, search]);

  const openDetail = async (record) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await axios.get(`/admin/outlet/${record._id}`);
      setDetail(res?.data || { outlet: record });
    } catch (e) {
      setDetail({ outlet: record });
      message.error('Could not load full details. Showing list data.');
    } finally {
      setDetailLoading(false);
    }
  };

  const approve = async (id) => {
    if (!id) return;
    setApproving(id);
    try {
      await axios.post(`/admin/outlets/${id}/verify`);
      message.success('Outlet verified');
      setDetailOpen(false);
      await fetchPending();
    } catch (e) {
      // interceptor toast
    } finally {
      setApproving(null);
    }
  };

  const outlet = detail?.outlet || {};
  const user = outlet.user || {};
  const photos = fileList(outlet.photo_library, outlet.outlet_images);
  const docs = fileList(outlet.document_image);

  const columns = [
    {
      title: 'Merchant',
      key: 'merchant',
      render: (_, r) => r.user?.full_name || '—',
    },
    {
      title: 'Outlet',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'City',
      dataIndex: 'city',
      key: 'city',
      render: (v) => v || '—',
    },
    {
      title: 'Mobile',
      key: 'mobile',
      render: (_, r) => r.mobile_number || r.user?.mobile_number || '—',
    },
    {
      title: 'Status',
      key: 'status',
      render: () => <Tag color="orange">Pending</Tag>,
    },
    {
      title: 'Action',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              openDetail(record);
            }}
          >
            View
          </Button>
          <Button
            type="primary"
            icon={<CheckOutlined />}
            loading={approving === record._id}
            onClick={(e) => {
              e.stopPropagation();
              approve(record._id);
            }}
          >
            Approve
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <PageShell
      title="Merchant verification"
      description="Open a row to review shop photos and GST/license documents, then approve."
      actions={
        <Button icon={<ReloadOutlined />} onClick={fetchPending} loading={loading}>
          Refresh
        </Button>
      }
    >
      <Card style={{ borderRadius: 12, border: `1px solid ${colors.border}` }}>
        <Input.Search
          placeholder="Search pending outlets"
          allowClear
          style={{ maxWidth: 360, marginBottom: 16 }}
          onSearch={setSearch}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Table
          rowKey={(r) => r._id}
          loading={loading}
          columns={columns}
          dataSource={filtered}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'No pending verifications' }}
          onRow={(record) => ({
            onClick: () => openDetail(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      <Modal
        title={outlet.name || 'Outlet details'}
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        width={720}
        confirmLoading={approving === outlet._id}
        okText="Approve outlet"
        okButtonProps={{
          icon: <CheckOutlined />,
          disabled: !outlet._id,
        }}
        onOk={() => approve(outlet._id)}
      >
        {detailLoading ? (
          <div style={{ padding: 24, textAlign: 'center' }}>Loading…</div>
        ) : (
          <>
            <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Merchant" span={2}>
                {user.full_name || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Email">{user.email || '—'}</Descriptions.Item>
              <Descriptions.Item label="Mobile">
                {outlet.mobile_number || user.mobile_number || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Category" span={2}>
                {outlet.category?.name ||
                  outlet.category?.category_name ||
                  '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Address" span={2}>
                {[outlet.address, outlet.city, outlet.state, outlet.pin_code]
                  .filter(Boolean)
                  .join(', ') || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Hours">
                {outlet.opening_time || '—'} – {outlet.closing_time || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Weekly off">
                {outlet.weekoff || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="License no.">
                {outlet.licenseNumber || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Valid till">
                {outlet.licenseValidTo
                  ? String(outlet.licenseValidTo).slice(0, 10)
                  : '—'}
              </Descriptions.Item>
            </Descriptions>

            <div style={{ marginBottom: 16 }}>
              <strong>Shop photos</strong>
              <div style={{ marginTop: 8 }}>
                {photos.length === 0 ? (
                  <span style={{ color: colors.muted }}>No photos uploaded</span>
                ) : (
                  <Image.PreviewGroup>
                    <Space wrap>
                      {photos.map((src) => (
                        <Image
                          key={src}
                          src={src}
                          width={96}
                          height={96}
                          style={{ objectFit: 'cover', borderRadius: 8 }}
                        />
                      ))}
                    </Space>
                  </Image.PreviewGroup>
                )}
              </div>
            </div>

            <div>
              <strong>GST / license documents</strong>
              <div style={{ marginTop: 8 }}>
                {docs.length === 0 ? (
                  <span style={{ color: colors.muted }}>No documents uploaded</span>
                ) : (
                  <Image.PreviewGroup>
                    <Space wrap>
                      {docs.map((src) => (
                        <Image
                          key={src}
                          src={src}
                          width={120}
                          height={120}
                          style={{ objectFit: 'cover', borderRadius: 8 }}
                        />
                      ))}
                    </Space>
                  </Image.PreviewGroup>
                )}
              </div>
            </div>
          </>
        )}
      </Modal>
    </PageShell>
  );
};

export default MerchantVerification;
